import { useState, useEffect } from 'react'

import { Button, Grid, Select, Span, TableFooter } from 'components'
import { DeleteIcon } from 'components/Icons'
import { useModal, useLoading } from 'hooks'
import { CostCenterServices } from 'services/NGT/CostCenter'
import { ParamCockpitServices } from 'services/NGT/Parameterization'
import { SPACING, TYPOGRAPHY } from 'utils'

import { plantCenterData } from '../../mock/plant-center'
import {
  TableContainerStyled,
  TableStyled,
  TableHeadStyled,
  TableRowStyled,
  TableCellStyled,
  TableBodyStyled
} from './PlantCenterTable.styles'

export const PlantCenterTable = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [costCenters, setCostCenter] = useState<any[]>([])
  const [plantCentersList, setPlantCentersList] = useState<any[]>([])
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [selectedPlantCenter, setSelectedPlantCenter] = useState(0)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getCostCenter = () => {
    setLoading('loading')
    CostCenterServices()
      .allCostCenter()
      .then(({ data }) => {
        setCostCenter(data.entity)
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar a lista de Centro Plantas',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const getPlantCenterList = () => {
    setLoading('loading')
    ParamCockpitServices()
      .getCentroPlantas()
      .then(({ data }) => {
        setPlantCentersList(data)
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar Centro Plantas cadastradas',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const handleChangePlantCenter = (e) => {
    setSelectedPlantCenter(e.target.value)
  }

  const addNewPlantCenter = () => {
    if (
      plantCentersList?.find((el) => el.idCentroPlanta === selectedPlantCenter)
    ) {
      showModal({
        hideButtonCancel: true,
        message: 'Este Centro Planta já foi incluído anteriormente, verifique!',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
    } else {
      setLoading('loading')
      ParamCockpitServices()
        .createCentroPlantas({ centroPlantaId: selectedPlantCenter })
        .then(() => {
          getPlantCenterList()
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Centro Planta incluído com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          setSelectedPlantCenter(0)
        })
        .catch((error) => {
          showModal({
            hideButtonCancel: true,
            message: 'Erro ao incluir Centro Planta, tente novamente!',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleDelete = (idCockpitCentroPlanta: number) => {
    setLoading('loading')
    ParamCockpitServices()
      .deleteCentroPlantas(idCockpitCentroPlanta)
      .then((response) => {
        if (response.status === 200) {
          showModal({
            hideButtonCancel: true,
            message: 'Excluído com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          getPlantCenterList()
        }
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao excluir Centro Planta, tente novamente!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
      .finally(() => {
        setLoading('idle')
      })
  }

  useEffect(() => {
    getCostCenter()
    getPlantCenterList()
  }, [])

  return (
    <>
      <Grid
        gridCols="3.5fr 0.4fr 4fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <Select
          value={selectedPlantCenter}
          onChange={handleChangePlantCenter}
          formValue="idCentroPlanta"
          label="Centro Planta"
          valueMenu="idCentroPlanta"
          labelMenu="txDescricao"
          listOptions={costCenters}
          required
        />
        <Button text="+" onClick={addNewPlantCenter} />
      </Grid>

      <Span
        text="Para cálculo de risco na visão Cockpit, desconsiderar dados abaixo:"
        fontSize={TYPOGRAPHY.bodySize16}
      />
      <Grid
        gridCols="5fr 3fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <TableContainerStyled>
          <TableStyled stickyHeader>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled minwidth="18em">Centro Planta</TableCellStyled>
                <TableCellStyled>Ação</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? plantCentersList?.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                : plantCentersList
              )?.map((row) => (
                <TableRowStyled key={row.idCockpitCentroPlanta}>
                  <TableCellStyled>{row.nmCentroPlanta}</TableCellStyled>
                  <TableCellStyled>
                    <DeleteIcon
                      onClick={() =>
                        showModal({
                          handleConfirm: () =>
                            handleDelete(row.idCockpitCentroPlanta),
                          message: 'Confirma exclusão (S/N)?',
                          txtButtonCancel: 'NÃO',
                          txtButtonConfirm: 'SIM',
                          type: 'alert'
                        })
                      }
                      cursor="pointer"
                    />
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>
          <TableFooter
            page={page}
            rows={plantCentersList}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="offline"
          />
        </TableContainerStyled>
      </Grid>
    </>
  )
}
